import { useCallback, useEffect, useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import apiService from '../../services/ApiService';
import { apiRoutes } from '../../shared/apiRoutes';

type TwoFactorStatus = {
  enabled: boolean;
  paired: boolean;
  pairingCode: string | null;
  secondsRemaining: number;
};

const emptyStatus: TwoFactorStatus = {
  enabled: false,
  paired: false,
  pairingCode: null,
  secondsRemaining: 0,
};

function readStatus(response: unknown): TwoFactorStatus {
  const body = (response as { data?: TwoFactorStatus })?.data ?? response;
  const row = body as TwoFactorStatus;
  return {
    enabled: Boolean(row?.enabled),
    paired: Boolean(row?.paired),
    pairingCode: row?.pairingCode ?? null,
    secondsRemaining: Number(row?.secondsRemaining ?? 0),
  };
}

function formatCountdown(totalSeconds: number): string {
  const seconds = Math.max(0, totalSeconds);
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${rest.toString().padStart(2, '0')}`;
}

function formatPairingCode(code: string): string {
  const digits = code.replace(/\D/g, '');
  return digits.length > 3 ? `${digits.slice(0, 3)} ${digits.slice(3)}` : digits;
}

interface TwoFactorSettingsProps {
  isOpen: boolean;
}

const TwoFactorSettings: React.FC<TwoFactorSettingsProps> = ({ isOpen }) => {
  const [status, setStatus] = useState<TwoFactorStatus>(emptyStatus);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const downloadApk = async () => {
    if (downloading) return;
    setDownloading(true);
    setError(null);
    try {
      const response = await apiService.getBlob(apiRoutes.auth.authenticatorApk);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'TigerAuthenticator.apk';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const row = err as { error?: string; message?: string };
      setError(row?.error || row?.message || 'Could not download the authenticator app.');
    } finally {
      setDownloading(false);
    }
  };

  const load = useCallback(async () => {
    const response = await apiService.get(apiRoutes.auth.twoFactor);
    setStatus(readStatus(response));
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    load()
      .catch((err: { message?: string }) => {
        if (!cancelled) {
          setError(err?.message || 'Could not load authenticator settings.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, load]);

  useEffect(() => {
    if (!isOpen || !status.enabled || status.paired) {
      return;
    }
    const poll = window.setInterval(() => {
      load().catch(() => undefined);
    }, 4000);
    const tick = window.setInterval(() => {
      setStatus((prev) => (
        prev.enabled && !prev.paired
          ? { ...prev, secondsRemaining: Math.max(0, prev.secondsRemaining - 1) }
          : prev
      ));
    }, 1000);
    return () => {
      window.clearInterval(poll);
      window.clearInterval(tick);
    };
  }, [isOpen, status.enabled, status.paired, status.pairingCode, load]);

  const run = async (action: () => Promise<unknown>) => {
    setLoading(true);
    setError(null);
    try {
      const response = await action();
      if (response) {
        setStatus(readStatus(response));
      } else {
        await load();
      }
      setConfirmDisable(false);
    } catch (err: any) {
      setError(err?.message || 'Could not update two-way authentication.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-2 border-t border-gray-200 space-y-3">
      <div>
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-gray-900">Two-way authentication</h4>
          <button
            type="button"
            title="Download authenticator app"
            aria-label="Download authenticator app"
            disabled={downloading}
            onClick={downloadApk}
            className="rounded p-1 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          On the phone, sign in once with your username, password, and the code below. After that, each website sign-in also needs the 6-digit code shown on that phone. The code changes every 2 minutes.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {error}
        </div>
      )}

      {!status.enabled && (
        <button
          type="button"
          disabled={loading}
          onClick={() => run(() => apiService.post(apiRoutes.auth.twoFactorEnable, {}))}
          className="w-full rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Please wait...' : 'Enable two-way authentication'}
        </button>
      )}

      {status.enabled && !status.paired && status.pairingCode && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-4 text-center">
          <p className="text-xs font-medium text-blue-800 mb-2">
            Enter this code in the authenticator app with your username and password
          </p>
          <p className="text-4xl font-bold tracking-[0.25em] text-blue-900">
            {formatPairingCode(status.pairingCode)}
          </p>
          <p className="text-xs text-blue-700 mt-2">
            Expires in {formatCountdown(status.secondsRemaining)}
          </p>
        </div>
      )}

      {status.enabled && status.paired && (
        <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          Authenticator app is linked. Login will ask for the code from that phone.
        </div>
      )}

      {status.enabled && (
        <div className="flex flex-col gap-2">
          {status.paired && (
            <button
              type="button"
              disabled={loading}
              onClick={() => run(() => apiService.post(apiRoutes.auth.twoFactorRelink, {}))}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Link a different phone
            </button>
          )}
          {!confirmDisable ? (
            <button
              type="button"
              disabled={loading}
              onClick={() => setConfirmDisable(true)}
              className="w-full rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              Disable two-way authentication
            </button>
          ) : (
            <button
              type="button"
              disabled={loading}
              onClick={() => run(async () => {
                await apiService.post(apiRoutes.auth.twoFactorDisable, {});
                return emptyStatus;
              })}
              className="w-full rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              Confirm disable
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TwoFactorSettings;
